import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from '#environments';
import {
    RolePermissions,
    SecurableResource,
    UpdateRolePermissionsRequest,
    SyncEntraGroupsResult,
    SyncRoleUsersResult,
    SyncAllUsersResult,
    GetRoleUsersResponse,
    WorkflowDefinitionSummary,
    WorkflowDefinitionDetail,
} from '#models';

@Injectable({ providedIn: 'root' })
export class AdminService {
    private _http = inject(HttpClient);
    private _base = `${environment.apiBaseUrl}/v1/admin`;

    // ── Gap C: Role Permissions ──────────────────────────────────────────────

    // Shared cache so concurrent component mounts (route change) reuse one in-flight request
    private _rolesCache$?: Observable<RolePermissions[]>;
    private _rolesCacheExpiry = 0;

    getAllRolePermissions(): Observable<RolePermissions[]> {
        if (!this._rolesCache$ || Date.now() > this._rolesCacheExpiry) {
            this._rolesCache$ = this._http.get<RolePermissions[]>(`${this._base}/roles`)
                .pipe(shareReplay({ bufferSize: 1, refCount: false }));
            this._rolesCacheExpiry = Date.now() + 30_000;
        }
        return this._rolesCache$;
    }

    invalidateRolesCache(): void {
        this._rolesCache$ = undefined;
        this._rolesCacheExpiry = 0;
    }

    getRolePermissionDefaults(): Observable<Record<string, Record<string, string[]>>> {
        return this._http.get<Record<string, Record<string, string[]>>>(`${this._base}/roles/defaults`);
    }

    getSecurables(): Observable<SecurableResource[]> {
        return this._http.get<SecurableResource[]>(`${this._base}/roles/securables`);
    }

    updateRolePermissions(roleId: string, dto: UpdateRolePermissionsRequest): Observable<RolePermissions> {
        return this._http.put<RolePermissions>(`${this._base}/roles/${encodeURIComponent(roleId)}/permissions`, dto);
    }

    syncEntraGroups(): Observable<SyncEntraGroupsResult> {
        return this._http.post<SyncEntraGroupsResult>(`${this._base}/roles/sync-entra-groups`, {});
    }

    /** Syncs Entra app role manifest — imports new roles, marks db-only / entra-only discrepancies. */
    syncEntraAppRoles(): Observable<RolePermissions[]> {
        return this._http.post<RolePermissions[]>(`${this._base}/roles/sync-entra-app-roles`, {});
    }

    /** Updates the Entra app role value string for a role (remapping after an Entra rename). */
    updateAppRoleValue(roleId: string, appRoleValue: string): Observable<RolePermissions> {
        return this._http.patch<RolePermissions>(`${this._base}/roles/${encodeURIComponent(roleId)}/app-role-value`, { appRoleValue });
    }

    /** Deletes a role. Rejected if any UserRoleScope rows still reference it. */
    deleteRole(roleId: string): Observable<void> {
        return this._http.delete<void>(`${this._base}/roles/${encodeURIComponent(roleId)}`);
    }

    /** Fetches Entra group members, pre-provisions AppUser records, removes stale UserRoleScope rows. */
    syncRoleUsers(roleId: string): Observable<SyncRoleUsersResult> {
        return this._http.post<SyncRoleUsersResult>(`${this._base}/roles/${encodeURIComponent(roleId)}/sync-users`, {});
    }

    /** Syncs users from all Entra groups (all roles), provisions missing users, updates scopes, and deletes orphaned users. */
    syncAllUsers(): Observable<SyncAllUsersResult> {
        return this._http.post<SyncAllUsersResult>(`${this._base}/sync-all-users`, {});
    }

    /** Retrieves users assigned to a role (via UserRoleScope). Returns unique users sorted by DisplayName. */
    getRoleUsers(roleId: string): Observable<GetRoleUsersResponse> {
        return this._http.get<GetRoleUsersResponse>(`${this._base}/roles/${encodeURIComponent(roleId)}/users`);
    }

    // ── Gap D: Workflow Definitions ──────────────────────────────────────────

    getWorkflowDefinitions(): Observable<WorkflowDefinitionSummary[]> {
        return this._http.get<WorkflowDefinitionSummary[]>(`${this._base}/workflow-definitions`);
    }

    getWorkflowDefinition(workflowType: string): Observable<WorkflowDefinitionDetail> {
        return this._http.get<WorkflowDefinitionDetail>(`${this._base}/workflow-definitions/${workflowType}`);
    }

    updateWorkflowDefinition(workflowType: string, definitionJson: string): Observable<WorkflowDefinitionDetail> {
        return this._http.put<WorkflowDefinitionDetail>(
            `${this._base}/workflow-definitions/${workflowType}`,
            { definitionJson }
        );
    }
}
